import * as c from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { TranslateOptions, translateText } from 'src/utils/translateText'

import { BracketLeft } from '../icons/bracket-left.icon'
import { BracketRight } from '../icons/bracket-right.icon'
import { BuildingIcon } from '../icons/building.icon'
import { CalendarIcon } from '../icons/calendar.icon'
import { ColorPaletteIcon } from '../icons/color-palette.icon'
import { ContractIcon } from '../icons/contract.icon'
import { CreditCardIcon } from '../icons/credit-card.icon'
import { GroundFloorIcon } from '../icons/ground-floor.icon'
import {
  CreditCardListIcon,
  PixBoletoListIcon,
} from '../icons/payment-type.icon'

interface ProfilesProps {
  profilesBanner: {
    title: TranslateOptions
    image: string
    profiles: CardProfileProps[]
    idSection?: string
  }
}

interface CardProfileProps {
  icon: keyof typeof icons
  title: TranslateOptions
  description: TranslateOptions
  cta: TranslateOptions
  isLink: boolean
  url?: string
}

interface ModalDataInterface {
  title: string
  text: string
  Icon: (props: any) => JSX.Element
}

const icons = {
  building: BuildingIcon,
  calendar: CalendarIcon,
  'color-palette': ColorPaletteIcon,
} as const

export default function Profiles({ profilesBanner }: ProfilesProps) {
  const desktopImage = `linear-gradient(90deg, #FFF 53.64%, rgba(255, 255, 255, 0.00) 53.65%), url(${profilesBanner?.image}) center / cover no-repeat, linear-gradient(180deg, #FA2F93 0%, #C32573 89.15%)`
  const mobileImage = `url(${profilesBanner?.image}), linear-gradient(180deg, #FFF 0, #FFF 346px, #FA2F93 346px)`

  return (
    <c.Container px={['s5', 0]} id={profilesBanner?.idSection}>
      <c.Flex
        h={['full', '632px']}
        w="100%"
        bg={[mobileImage, desktopImage]}
        bgPosition={['top right', 'right']}
        bgRepeat="no-repeat"
        bgSize={['198px, 100%', 'contain']}
        borderRadius="20px"
        direction={['column-reverse', 'row']}
      >
        <c.Box
          position="relative"
          w={['100%', '640px']}
          py={['s5', 'l2']}
          pl={['s5', 'l2']}
          pr={['s5', 0]}
          borderRadius={['0 0 20px 20px', '20px 0 0 20px']}
        >
          <c.Flex
            position={['initial', 'absolute']}
            h={['100%', '552px']}
            w={['100%', '794px']}
            direction={['column-reverse', 'row']}
            flexWrap="wrap"
            justify="space-between"
            gap="s5"
          >
            <c.Heading
              textTransform="uppercase"
              textAlign={['left', 'right']}
              fontSize="3.75em"
              fontWeight="bold"
              color="primary.500"
              w={['100%', '386px']}
              h={['346px, "266px"']}
              pt={['s5', 's2']}
              pb={['l2', 's2']}
              px={[0, 's4']}
              order={[1, 0]}
            >
              {translateText(profilesBanner?.title)}
            </c.Heading>

            {profilesBanner?.profiles?.map((profile, i) => (
              <CardProfile {...profile} key={i} />
            ))}
          </c.Flex>
        </c.Box>
      </c.Flex>
    </c.Container>
  )
}

function CardProfile(props: CardProfileProps) {
  return (
    <c.Flex
      p="s5"
      h={['full', '266px']}
      w={['full', '386px']}
      maxW={['full', '386px']}
      bg="black.0"
      borderRadius="20px"
      direction="column"
      align="center"
      justify="center"
      shadow="lg"
    >
      <c.Box
        as="span"
        p="s2"
        h="40px"
        w="40px"
        bg="primary.50"
        borderRadius="8px"
      >
        <c.Icon as={icons[props.icon]} color="primary.500" h="" w="" />
      </c.Box>
      <c.Heading fontSize="h-md.0" mt="s3" mb="s2">
        {translateText(props.title)}
      </c.Heading>
      <c.Text fontSize="t-md.0" textAlign="center" mb="m3">
        {translateText(props.description)}
      </c.Text>

      {props.isLink ? (
        <c.Button
          as={c.Link}
          isExternal
          href={props?.url}
          variant="secondary"
          fontSize="t-md.0"
          maxH="37px"
          border="1px solid"
          borderColor="primary.100"
          borderRadius="full"
        >
          {translateText(props.cta)}
        </c.Button>
      ) : (
        <AboutHousiModal {...props} />
      )}
    </c.Flex>
  )
}

function AboutHousiModal(props: CardProfileProps) {
  const { t } = useTranslation('home-v3')
  const { isOpen, onOpen, onClose } = c.useDisclosure()

  const items: ModalDataInterface[] = [
    {
      title: t('home-v3:MODAL_INFO.apartament.0'),
      text: t('home-v3:MODAL_INFO.apartament.1'),
      Icon: GroundFloorIcon,
    },
    {
      title: t('home-v3:MODAL_INFO.time.0'),
      text: t('home-v3:MODAL_INFO.time.1'),
      Icon: CalendarIcon,
    },
    {
      title: t('home-v3:MODAL_INFO.rules.0'),
      text: t('home-v3:MODAL_INFO.rules.1'),
      Icon: ContractIcon,
    },
    {
      title: t('home-v3:MODAL_INFO.appspace.0'),
      text: t('home-v3:MODAL_INFO.appspace.1'),
      Icon: ContractIcon,
    },
    {
      title: t('home-v3:MODAL_INFO.payment.0'),
      text: t('home-v3:MODAL_INFO.payment.1'),
      Icon: CreditCardIcon,
    },
  ]

  return (
    <>
      <c.Button
        onClick={onOpen}
        variant="secondary"
        fontSize="t-md.0"
        maxH="37px"
        border="1px solid"
        borderColor="primary.100"
        borderRadius="full"
      >
        {translateText(props.cta)}
      </c.Button>

      <c.Modal isOpen={isOpen} onClose={onClose}>
        <c.ModalOverlay />
        <c.ModalContent borderRadius="20px">
          <c.ModalCloseButton
            top={8}
            right={8}
            color="black.500"
            bg="black.100"
          />

          <c.ModalBody py="l2" px={['s5', 'l2']}>
            <c.Heading size="md" mb="s5">
              {t('home-v3:MODAL_INFO.heading.0')}
            </c.Heading>
            <c.Text color="black.400" size="md" mb="m4">
              {t('home-v3:MODAL_INFO.heading.1')}
            </c.Text>

            <c.Flex direction="column" gap="s5">
              {items.map((item, index) => (
                <c.Flex direction="column" gap="s3" key={index}>
                  <c.Flex align="center" gap="s3">
                    <c.Flex align="center" gap="s1">
                      <c.Icon
                        as={BracketRight}
                        color="primary.500"
                        h="38px"
                        w="10px"
                      />
                      <c.Icon
                        as={item.Icon}
                        color="black.400"
                        h="24px"
                        w="24px"
                      />
                      <c.Icon
                        as={BracketLeft}
                        color="primary.500"
                        h="38px"
                        w="10px"
                      />
                    </c.Flex>
                    <c.Heading size="sm">{item.title}</c.Heading>
                  </c.Flex>
                  <c.Text color="black.400" size="md">
                    {item.text}
                  </c.Text>
                </c.Flex>
              ))}

              <c.Flex gap="l2">
                <c.Flex w="159px" direction="column">
                  <c.Text size="md" fontWeight="bold">
                    {t('home-v3:MODAL_INFO.payment_options.0')}
                  </c.Text>
                  <c.Text size="sm" color="black.400" mb="s4">
                    {t('home-v3:MODAL_INFO.payment_options.1')}
                  </c.Text>
                  <CreditCardListIcon />
                </c.Flex>

                <c.Flex direction="column" justify="space-between">
                  <c.Box>
                    <c.Text size="md" fontWeight="bold">
                      {t('home-v3:MODAL_INFO.payment_options.2')}
                    </c.Text>
                    <c.Text size="sm" mb="s4">
                      {t('home-v3:MODAL_INFO.payment_options.3')}
                    </c.Text>
                  </c.Box>
                  <PixBoletoListIcon />
                </c.Flex>
              </c.Flex>
            </c.Flex>

            <c.Flex mt="l2" direction="column" gap="s3">
              <c.Button
                as={Link}
                to="/search"
                variant="primary"
                w="full"
                borderRadius="full"
              >
                {t('home-v3:MODAL_INFO.cta')}
              </c.Button>
              <c.Button
                onClick={onClose}
                variant="light"
                w="full"
                bg="transparent"
                borderRadius="full"
              >
                Fechar
              </c.Button>
            </c.Flex>
          </c.ModalBody>
        </c.ModalContent>
      </c.Modal>
    </>
  )
}
